import { useQuery } from "@apollo/client";
import { Typography } from "@mui/material";
import { useMemo, ReactElement, useState } from "react";
import {
  NumberParam,
  StringParam,
  useQueryParams,
  withDefault,
} from "use-query-params";

import {
  GetActivePricingBundlesQuery,
  GetActiveSkusQuery,
  ListAllActivePricingAssignmentsQuery,
  ListAllActivePricingAssignmentsQueryVariables,
  PricingAssignmentSort,
  ListTenantsWithSubaccountsQuery,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import PageContainer from "~frontend/components/PageContainer";
import { TableControls } from "~frontend/components/Table";
import {
  GET_ACTIVE_PRICING_BUNDLES,
  GET_ACTIVE_SKUS,
  LIST_ALL_ACTIVE_PRICING_ASSIGNMENTS,
} from "~frontend/graphql/pricingBundle";
import { LIST_TENANTS_WITH_SUBACCOUNTS } from "~frontend/graphql/tenant";
import { useBreadcrumbs } from "~frontend/providers/BreadcrumbsProvider";
import useDebounce from "~frontend/util/useDebounce";

import {
  ChangePricingDialog,
  ChangePricingDialogMode,
} from "../../organization/pricing/ChangePricingDialog";
import { ArchivePricingBundleDialog } from "./ArchivePricingBundleDialog";
import { NewPricingBundleDialog } from "./NewPricingBundleDialog";
import PricingAssignmentsTable, {
  ChangePricingTarget,
} from "./PricingAssignmentsTable";
import PricingBundlesTable from "./PricingBundlesTable";

type DialogState =
  | {
      kind: "tenant";
      tenantId: string;
      tenantName: string;
      currentBundleId: string;
    }
  | {
      kind: "subaccount";
      tenantId: string;
      subaccountId: string;
      subaccountName: string;
      subaccountSlug: string;
      isWeText: boolean;
    }
  | { kind: "bulk" }
  | { kind: "new" }
  | { kind: "archive"; bundleId: string; bundleName: string };

const BUNDLE_SORTS = [
  { value: "name_asc", label: "Pricing Option, A-Z" },
  { value: "name_desc", label: "Pricing Option, Z-A" },
] as const;

type BundleSort = (typeof BUNDLE_SORTS)[number]["value"];

const WETEXT_FILTERS = [
  { value: "all", label: "All" },
  { value: "wetext", label: "WeText" },
  { value: "not_wetext", label: "Not WeText" },
] as const;

type WeTextFilter = (typeof WETEXT_FILTERS)[number]["value"];

const ASSIGNMENT_SORTS = [
  { value: PricingAssignmentSort.SlugAsc, label: "Slug, A-Z" },
  { value: PricingAssignmentSort.SlugDesc, label: "Slug, Z-A" },
] as const;

type AssignmentSort = (typeof ASSIGNMENT_SORTS)[number]["value"];

const ALL_PRICING_FILTER = "all";
const CUSTOM_PRICING_FILTER = "__custom__";
const CUSTOM_PRICING_LABEL = "Custom Pricing";

function validateBundleSort(sort: string): BundleSort {
  return (
    BUNDLE_SORTS.find(s => s.value === sort)?.value ?? BUNDLE_SORTS[0].value
  );
}

function validateWeTextFilter(filter: string): WeTextFilter {
  return (
    WETEXT_FILTERS.find(f => f.value === filter)?.value ??
    WETEXT_FILTERS[0].value
  );
}

function validateAssignmentSort(sort: string): AssignmentSort {
  return (
    ASSIGNMENT_SORTS.find(s => s.value === sort)?.value ??
    ASSIGNMENT_SORTS[0].value
  );
}

export default function Pricing(): ReactElement {
  useBreadcrumbs([{ label: "Pricing", path: "/staff/pricing" }]);

  const [queryParams, setQueryParams] = useQueryParams({
    bundle_page: withDefault(NumberParam, 0),
    bundle_page_size: withDefault(NumberParam, 50),
    bundle_sort: withDefault(StringParam, BUNDLE_SORTS[0].value),
    wetext: withDefault(StringParam, WETEXT_FILTERS[0].value),
    assign_page: withDefault(NumberParam, 0),
    assign_page_size: withDefault(NumberParam, 50),
    assign_sort: withDefault(StringParam, ASSIGNMENT_SORTS[0].value),
    assign_pricing: withDefault(StringParam, ALL_PRICING_FILTER),
    assign_search: withDefault(StringParam, ""),
  });
  const {
    bundle_page: bundlePage,
    bundle_page_size: bundlePageSize,
    bundle_sort: bundleSort,
    wetext: weTextFilter,
    assign_page: assignmentsPage,
    assign_page_size: assignmentsPageSize,
    assign_sort: assignmentsSort,
    assign_pricing: assignmentsPricingFilter,
    assign_search: assignmentsSearch,
  } = queryParams;

  const validatedBundleSort = validateBundleSort(bundleSort);
  const validatedWeTextFilter = validateWeTextFilter(weTextFilter);
  const validatedAssignmentSort = validateAssignmentSort(assignmentsSort);

  const [dialogState, setDialogState] = useState<DialogState | null>(null);

  const bundlesQuery = useQuery<GetActivePricingBundlesQuery>(
    GET_ACTIVE_PRICING_BUNDLES
  );
  const skusQuery = useQuery<GetActiveSkusQuery>(GET_ACTIVE_SKUS);
  const tenantsQuery = useQuery<ListTenantsWithSubaccountsQuery>(
    LIST_TENANTS_WITH_SUBACCOUNTS,
    { skip: dialogState?.kind !== "bulk" }
  );

  function handleChangePricing(target: ChangePricingTarget) {
    if (target.kind === "tenant") {
      setDialogState({
        kind: "tenant",
        tenantId: target.tenantId,
        tenantName: target.tenantName,
        currentBundleId: target.currentBundleId,
      });
    } else {
      setDialogState({
        kind: "subaccount",
        tenantId: target.tenantId,
        subaccountId: target.subaccountId,
        subaccountName: target.subaccountName,
        subaccountSlug: target.subaccountSlug,
        isWeText: target.isWeText,
      });
    }
  }

  function handleCloseDialog() {
    setDialogState(null);
  }

  const debouncedAssignmentsSearch = useDebounce(assignmentsSearch, 300);
  const assignmentsQuery = useQuery<
    ListAllActivePricingAssignmentsQuery,
    ListAllActivePricingAssignmentsQueryVariables
  >(LIST_ALL_ACTIVE_PRICING_ASSIGNMENTS, {
    variables: {
      input: {
        search: debouncedAssignmentsSearch || null,
        pricingBundleName:
          assignmentsPricingFilter === ALL_PRICING_FILTER ||
          assignmentsPricingFilter === CUSTOM_PRICING_FILTER
            ? null
            : assignmentsPricingFilter,
        customPricingOnly:
          assignmentsPricingFilter === CUSTOM_PRICING_FILTER ? true : null,
        sort: validatedAssignmentSort,
        page: assignmentsPage,
        pageSize: assignmentsPageSize,
      },
    },
  });

  const filteredBundles = useMemo(() => {
    const all = (bundlesQuery.data?.getActivePricingBundles ?? []).filter(
      b => !b.isSingleUse
    );

    const filtered =
      validatedWeTextFilter === "wetext"
        ? all.filter(b => b.isWeText)
        : validatedWeTextFilter === "not_wetext"
          ? all.filter(b => !b.isWeText)
          : all;

    return filtered.slice().sort((a, b) => {
      if (validatedBundleSort === "name_asc") {
        return a.name.localeCompare(b.name);
      }
      return b.name.localeCompare(a.name);
    });
  }, [bundlesQuery.data, validatedBundleSort, validatedWeTextFilter]);

  const pricingFilterOptions = useMemo(() => {
    const all = (bundlesQuery.data?.getActivePricingBundles ?? []).filter(
      b => !b.isSingleUse
    );
    const names = [...new Set(all.map(b => b.name))].sort((a, b) =>
      a.localeCompare(b)
    );
    return [
      { value: ALL_PRICING_FILTER, label: "All" },
      { value: CUSTOM_PRICING_FILTER, label: CUSTOM_PRICING_LABEL },
      ...names.map(name => ({ value: name, label: name })),
    ];
  }, [bundlesQuery.data]);

  return (
    <PageContainer maxWidth="xl">
      <Typography variant="display0Bold" component="h1" sx={{ mb: 1 }}>
        Pricing Options
      </Typography>
      <TableControls
        variant="compact"
        actionButtons={[
          {
            id: "add-pricing-option",
            label: "Add Pricing Option",
            onClick: () => setDialogState({ kind: "new" }),
          },
        ]}
        sort={{
          value: bundleSort,
          onChange: value => {
            setQueryParams({
              bundle_sort: value as BundleSort,
              bundle_page: 0,
            });
          },
          options: BUNDLE_SORTS.map(s => ({ value: s.value, label: s.label })),
        }}
        filters={[
          {
            id: "wetext",
            label: "WeText",
            value: weTextFilter,
            onChange: (value: string | null) => {
              setQueryParams({
                wetext: (value ?? WETEXT_FILTERS[0].value) as WeTextFilter,
                bundle_page: 0,
              });
            },
            options: WETEXT_FILTERS.map(f => ({
              value: f.value,
              label: f.label,
            })),
          },
        ]}
      />
      <AsyncWrapper loading={bundlesQuery.loading} errors={bundlesQuery.error}>
        <PricingBundlesTable
          bundles={filteredBundles}
          page={bundlePage}
          pageSize={bundlePageSize}
          onChangePage={newPage => setQueryParams({ bundle_page: newPage })}
          onChangePageSize={newSize =>
            setQueryParams({ bundle_page_size: newSize, bundle_page: 0 })
          }
          onArchive={bundle =>
            setDialogState({
              kind: "archive",
              bundleId: bundle.id,
              bundleName: bundle.name,
            })
          }
        />
      </AsyncWrapper>

      <Typography variant="display0Bold" component="h1" sx={{ mt: 4, mb: 1 }}>
        Tenants
      </Typography>
      <TableControls
        sort={{
          value: assignmentsSort,
          onChange: value => {
            setQueryParams({
              assign_sort: value as AssignmentSort,
              assign_page: 0,
            });
          },
          options: ASSIGNMENT_SORTS.map(s => ({
            value: s.value,
            label: s.label,
          })),
        }}
        filters={[
          {
            id: "assign_pricing",
            label: "Pricing Option",
            value: assignmentsPricingFilter,
            onChange: (value: string | null) => {
              setQueryParams({
                assign_pricing: value ?? ALL_PRICING_FILTER,
                assign_page: 0,
              });
            },
            options: pricingFilterOptions,
          },
        ]}
        search={{
          value: assignmentsSearch,
          onChange: value => {
            setQueryParams({ assign_search: value, assign_page: 0 });
          },
          placeholder: "Search tenant or subaccount name or slug",
        }}
        actionButtons={[
          {
            id: "bulk-change-pricing",
            label: "Bulk Change Pricing",
            onClick: () => setDialogState({ kind: "bulk" }),
          },
        ]}
      />
      <AsyncWrapper
        data={assignmentsQuery.data}
        loading={assignmentsQuery.loading}
        errors={assignmentsQuery.error}
      >
        {({ listAllActivePricingAssignments }) => (
          <PricingAssignmentsTable
            tenants={listAllActivePricingAssignments.tenants ?? []}
            totalCount={listAllActivePricingAssignments.totalCount ?? 0}
            page={assignmentsPage}
            pageSize={assignmentsPageSize}
            onChangePage={newPage => setQueryParams({ assign_page: newPage })}
            onChangePageSize={newSize =>
              setQueryParams({ assign_page_size: newSize, assign_page: 0 })
            }
            onChangePricing={handleChangePricing}
          />
        )}
      </AsyncWrapper>

      {dialogState && (
        <PricingDialogs
          state={dialogState}
          bundles={bundlesQuery.data?.getActivePricingBundles ?? []}
          skus={skusQuery.data?.getActiveSkus ?? []}
          tenants={tenantsQuery.data?.listTenants ?? []}
          onClose={handleCloseDialog}
        />
      )}
    </PageContainer>
  );
}

function PricingDialogs({
  state,
  bundles,
  skus,
  tenants,
  onClose,
}: {
  state: DialogState;
  bundles: GetActivePricingBundlesQuery["getActivePricingBundles"];
  skus: GetActiveSkusQuery["getActiveSkus"];
  tenants: ListTenantsWithSubaccountsQuery["listTenants"];
  onClose: () => void;
}): ReactElement | null {
  // Filter out single-use bundles (existing custom bundles) — same convention
  // the org-page dialog uses; new custom bundles can still be minted via the
  // "Custom Pricing" sentinel in the dropdown.
  const visibleBundles = bundles.filter(b => !b.isSingleUse);

  switch (state.kind) {
    case "new": {
      return (
        <NewPricingBundleDialog
          open
          onClose={onClose}
          skus={skus}
          bundles={bundles}
        />
      );
    }
    case "archive": {
      return (
        <ArchivePricingBundleDialog
          open
          onClose={onClose}
          bundleId={state.bundleId}
          bundleName={state.bundleName}
          bundles={visibleBundles}
        />
      );
    }
    case "tenant": {
      return (
        <ChangePricingDialog
          open
          onClose={onClose}
          bundles={visibleBundles}
          skus={skus}
          tenantId={state.tenantId}
          tenantName={state.tenantName}
          currentBundleId={state.currentBundleId}
        />
      );
    }
    case "subaccount": {
      return (
        <ChangePricingDialog
          open
          onClose={onClose}
          bundles={visibleBundles}
          skus={skus}
          mode={ChangePricingDialogMode.Subaccount}
          tenantId={state.tenantId}
          lockedSubaccountId={state.subaccountId}
          subaccounts={[
            {
              id: state.subaccountId,
              name: state.subaccountName,
              slug: state.subaccountSlug,
              isWeText: state.isWeText,
            },
          ]}
        />
      );
    }
  }

  return (
    <ChangePricingDialog
      open
      onClose={onClose}
      bundles={visibleBundles}
      skus={skus}
      mode={ChangePricingDialogMode.BulkTenants}
      tenants={tenants}
    />
  );
}
